// src/cockpit4/components/ContentOptimizationLoop.tsx
// ============================================
// P1-功能：数据驱动内容优化闭环（媒体工作室）
//
// 功能：
//   1. 发布后数据自动同步（阅读/点赞/评论/转发/粉丝增长）
//   2. 内容效果排行榜（爆款/良好/一般/差 四级，含数量与占比）
//   3. 爆款内容特征分析（标题长度/是否含数字/是否含疑问词/发布时段/内容形式/高频选题词）
//   4. AI 生成优化建议（标题优化/选题方向/发布时间/内容形式，基于历史数据）
//   5. 自动学习：分析结果沉淀为「学习规则」写入 localStorage，越用越懂什么内容效果好
//   6. 闭环流程可视化：发布 → 数据回收 → 分析 → 优化 → 再发布
//
// 数据来源：
//   - useDashboardData() 的 marketing.platforms（Record<MarketingPlatform, PlatformData>）
//     PlatformData.metrics 提供 views/likes/comments/shares/followersGain 等聚合指标
//     PlatformData.topContent 提供 TopContentItem[]（id/title/views/likes/publishedAt）
//   - localStorage（key: xeaos_content_optimization_rules）持久化「学习规则」
//   - 内置规则库 BUILTIN_RULES（标题/选题/发布时间/内容形式 四类种子建议）
//   - 效果分级标准（内置）：
//       爆款 views > 10000 且 likes/views > 0.05
//       良好 views > 1000  且 likes/views > 0.03
//       一般 views > 100
//       差   其他
//   - 无数据时显示空状态，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  RefreshCw,
  TrendingUp,
  Award,
  Lightbulb,
  BarChart3,
  Filter,
  Send,
  Activity,
  RotateCw,
  ArrowRight,
  Clock,
  Hash,
  Type,
  Tag,
  Loader2,
  Database,
  Sparkles,
} from "lucide-react";
import {
  useDashboardData,
  type MarketingPlatform,
  type TopContentItem,
} from "../../hooks/useDashboardData";

// ============================================
// 类型与常量
// ============================================

const RULES_STORAGE_KEY = "xeaos_content_optimization_rules";

/** 内容效果分级 */
type ContentGrade = "viral" | "good" | "normal" | "poor";

/** 分级元信息 */
interface GradeMeta {
  key: ContentGrade;
  label: string;
  color: string;
  icon: typeof Award;
}

/** 优化规则类别 */
type RuleCategory = "title" | "topic" | "timing" | "format";

/** 优化规则（内置 + 学习） */
interface OptimizationRule {
  id: string;
  category: RuleCategory;
  insight: string;
  suggestion: string;
  /** 置信度 0-100 */
  confidence: number;
  /** 样本量 */
  sampleSize: number;
  /** 学习时间 YYYY-MM-DD */
  learnedAt: string;
  /** 来源：内置 / 学习 */
  source: "builtin" | "learned";
}

/** 带平台与分级标记的内容项 */
interface ScoredContent extends TopContentItem {
  platform: MarketingPlatform;
  grade: ContentGrade;
}

/** 爆款特征分析结果 */
interface HotFeatureAnalysis {
  /** 爆款样本数 */
  totalCount: number;
  /** 平均标题长度 */
  avgTitleLength: number;
  /** 含数字比例 0-100 */
  numberRate: number;
  /** 含疑问词比例 0-100 */
  questionRate: number;
  /** 发布时段分布 */
  topTimeSlots: { slot: string; count: number }[];
  /** 内容形式分布 */
  topForms: { form: string; count: number }[];
  /** 高频选题词 */
  topKeywords: { word: string; count: number }[];
}

/** 平台中文名 */
const PLATFORM_LABEL: Record<MarketingPlatform, string> = {
  wechat_official: "公众号",
  xiaohongshu: "小红书",
  douyin_enterprise: "抖音企业号",
  douyin_personal: "抖音个人号",
  zhihu: "知乎",
  bilibili: "B站",
};

/** 平台对应的内容形式（用于爆款形式分析，基于平台特性推断） */
const PLATFORM_FORM: Record<MarketingPlatform, string> = {
  wechat_official: "图文长文",
  xiaohongshu: "图文笔记",
  douyin_enterprise: "短视频",
  douyin_personal: "短视频",
  zhihu: "问答长文",
  bilibili: "中长视频",
};

/** 分级元信息表 */
const GRADE_META: Record<ContentGrade, GradeMeta> = {
  viral: { key: "viral", label: "爆款", color: "#FBBF24", icon: Award },
  good: { key: "good", label: "良好", color: "#34D399", icon: TrendingUp },
  normal: { key: "normal", label: "一般", color: "#38BDF8", icon: BarChart3 },
  poor: { key: "poor", label: "差", color: "#6B7280", icon: Activity },
};

/** 规则类别元信息 */
const RULE_CATEGORY_META: Record<RuleCategory, { label: string; icon: typeof Type; color: string }> = {
  title: { label: "标题优化", icon: Type, color: "#38BDF8" },
  topic: { label: "选题方向", icon: Tag, color: "#A78BFA" },
  timing: { label: "发布时间", icon: Clock, color: "#FBBF24" },
  format: { label: "内容形式", icon: Hash, color: "#34D399" },
};

/** 内置规则库（种子建议，非假数据，作为冷启动默认建议） */
const BUILTIN_RULES: OptimizationRule[] = [
  {
    id: "builtin_title_number",
    category: "title",
    insight: "数字型标题点击率普遍较高",
    suggestion: "标题加入具体数字或列表式表达（如「3个方法」「提升200%」）",
    confidence: 60,
    sampleSize: 0,
    learnedAt: "",
    source: "builtin",
  },
  {
    id: "builtin_topic_pain",
    category: "topic",
    insight: "痛点选题更易引发共鸣与转发",
    suggestion: "围绕目标用户的核心痛点策划选题，避免泛泛而谈",
    confidence: 55,
    sampleSize: 0,
    learnedAt: "",
    source: "builtin",
  },
  {
    id: "builtin_timing_evening",
    category: "timing",
    insight: "晚 18-22 点为内容消费高峰",
    suggestion: "重要内容优先在晚间黄金时段发布",
    confidence: 50,
    sampleSize: 0,
    learnedAt: "",
    source: "builtin",
  },
  {
    id: "builtin_format_video",
    category: "format",
    insight: "视频形式互动率普遍高于纯图文",
    suggestion: "优先布局视频内容形式，图文作为补充",
    confidence: 50,
    sampleSize: 0,
    learnedAt: "",
    source: "builtin",
  },
];

/** 中文停用词（关键词提取过滤用） */
const STOPWORDS = new Set([
  "的", "了", "和", "是", "在", "我", "有", "就", "不", "都", "一", "与", "为", "对",
  "也", "这", "那", "你", "他", "她", "它", "们", "上", "下", "中", "里", "到", "去",
  "来", "把", "被", "让", "给", "向", "从", "跟", "及", "或", "但", "而", "却", "如",
  "若", "则", "于", "以", "其", "之", "着", "过", "地", "得", "很", "最", "更", "还",
  "再", "又", "才", "只", "已", "将", "会", "能", "可", "可以", "需要", "应该", "一个",
  "一些", "一种", "这个", "那个", "什么", "怎么", "为什么", "如何", "为何", "怎样",
  "吗", "呢", "啊", "吧", "哦", "嗯", "你的", "我的", "他的",
]);

/** 闭环流程节点定义 */
const LOOP_NODES: { label: string; desc: string; icon: typeof Send; color: string }[] = [
  { label: "发布", desc: "多平台分发", icon: Send, color: "#38BDF8" },
  { label: "数据回收", desc: "阅读/点赞/评论", icon: Database, color: "#34D399" },
  { label: "分析", desc: "效果分级/特征", icon: Activity, color: "#A78BFA" },
  { label: "优化", desc: "AI 建议生成", icon: Lightbulb, color: "#FBBF24" },
  { label: "再发布", desc: "迭代闭环", icon: RotateCw, color: "#FB923C" },
];

// ============================================
// 工具函数
// ============================================

/** YYYY-MM-DD */
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * 内容效果分级
 *   爆款 views > 10000 且 likes/views > 0.05
 *   良好 views > 1000  且 likes/views > 0.03
 *   一般 views > 100
 *   差   其他
 */
function classifyContent(item: TopContentItem): ContentGrade {
  const views = item.views || 0;
  const likes = item.likes || 0;
  const ratio = views > 0 ? likes / views : 0;
  if (views > 10000 && ratio > 0.05) return "viral";
  if (views > 1000 && ratio > 0.03) return "good";
  if (views > 100) return "normal";
  return "poor";
}

/** 判断标题是否含数字 */
function titleHasNumber(title: string): boolean {
  return /\d/.test(title);
}

/** 判断标题是否含疑问词 */
function titleHasQuestion(title: string): boolean {
  return /(为什么|怎么|如何|为何|怎样|吗|呢|\?|？)/.test(title);
}

/** 根据发布时间字符串获取时段标签 */
function getTimeSlot(dateStr: string): string {
  if (!dateStr) return "未知";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "未知";
  const h = d.getHours();
  if (h >= 6 && h < 11) return "早晨 06-10";
  if (h >= 11 && h < 14) return "中午 11-13";
  if (h >= 14 && h < 18) return "下午 14-17";
  if (h >= 18 && h < 23) return "晚间 18-22";
  return "深夜 23-05";
}

/** 从标题提取候选关键词（字母数字 token + 中文 2-gram，过滤停用词） */
function extractKeywords(title: string): string[] {
  const out: string[] = [];
  if (!title) return out;
  // 字母数字 token（长度 >= 2）
  const alphaNums = title.match(/[A-Za-z0-9]{2,}/g) || [];
  for (const t of alphaNums) out.push(t);
  // 中文片段：按非中文字符切分
  const cjkChunks = title.match(/[\u4e00-\u9fa5]+/g) || [];
  for (const chunk of cjkChunks) {
    // 2-gram 滑动窗口
    for (let i = 0; i < chunk.length - 1; i++) {
      const gram = chunk.slice(i, i + 2);
      // 含停用词字符的 2-gram 跳过
      if (STOPWORDS.has(gram) || STOPWORDS.has(gram[0]) || STOPWORDS.has(gram[1])) continue;
      out.push(gram);
    }
  }
  return out;
}

/** 安全读取 localStorage 中的学习规则 */
function loadLearnedRules(): OptimizationRule[] {
  try {
    const raw = localStorage.getItem(RULES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as OptimizationRule[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入 localStorage 学习规则 */
function saveLearnedRules(rules: OptimizationRule[]): void {
  try {
    localStorage.setItem(RULES_STORAGE_KEY, JSON.stringify(rules));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/**
 * 基于爆款特征分析生成「学习规则」
 * 样本量越大、特征越显著，置信度越高
 */
function generateLearnedRules(
  analysis: HotFeatureAnalysis,
  scopeLabel: string
): OptimizationRule[] {
  const rules: OptimizationRule[] = [];
  const n = analysis.totalCount;
  if (n === 0) return rules;

  // 置信度计算：基础 40 + 特征显著度 + 样本加成
  const conf = (rate: number) =>
    Math.max(40, Math.min(95, Math.round(40 + rate * 0.4 + Math.min(n, 20) * 1.5)));

  // 标题：含数字
  if (analysis.numberRate >= 50) {
    rules.push({
      id: "learned_title_number",
      category: "title",
      insight: `${scopeLabel}爆款标题中 ${analysis.numberRate}% 含数字`,
      suggestion: "标题加入具体数字（如「3个方法」「提升200%」）可提升点击",
      confidence: conf(analysis.numberRate),
      sampleSize: n,
      learnedAt: todayStr(),
      source: "learned",
    });
  }
  // 标题：含疑问词
  if (analysis.questionRate >= 50) {
    rules.push({
      id: "learned_title_question",
      category: "title",
      insight: `${scopeLabel}爆款标题中 ${analysis.questionRate}% 含疑问词`,
      suggestion: "使用疑问式标题（如何/为什么/怎样）引发好奇心",
      confidence: conf(analysis.questionRate),
      sampleSize: n,
      learnedAt: todayStr(),
      source: "learned",
    });
  }
  // 标题：长度
  if (analysis.avgTitleLength > 0) {
    if (analysis.avgTitleLength <= 12) {
      rules.push({
        id: "learned_title_length_short",
        category: "title",
        insight: `${scopeLabel}爆款标题平均 ${analysis.avgTitleLength} 字，偏短`,
        suggestion: "保持简洁标题（≤12 字）利于移动端展示与点击",
        confidence: conf(70),
        sampleSize: n,
        learnedAt: todayStr(),
        source: "learned",
      });
    } else {
      rules.push({
        id: "learned_title_length_long",
        category: "title",
        insight: `${scopeLabel}爆款标题平均 ${analysis.avgTitleLength} 字，偏长`,
        suggestion: "适当扩展标题长度，补充信息量与关键词",
        confidence: conf(60),
        sampleSize: n,
        learnedAt: todayStr(),
        source: "learned",
      });
    }
  }
  // 选题：高频词
  if (analysis.topKeywords.length > 0) {
    const kws = analysis.topKeywords.slice(0, 3).map((k) => k.word).join("/");
    rules.push({
      id: "learned_topic_keywords",
      category: "topic",
      insight: `${scopeLabel}爆款高频选题词：${kws}`,
      suggestion: `围绕「${kws}」方向延展选题，深挖相关长尾需求`,
      confidence: conf(Math.min(100, analysis.topKeywords[0].count * 20)),
      sampleSize: n,
      learnedAt: todayStr(),
      source: "learned",
    });
  }
  // 发布时间
  if (analysis.topTimeSlots.length > 0) {
    const top = analysis.topTimeSlots[0];
    rules.push({
      id: "learned_timing_slot",
      category: "timing",
      insight: `${scopeLabel}爆款高发时段：${top.slot}（${top.count}/${n}）`,
      suggestion: `优先在${top.slot}发布新内容，提升冷启动流量`,
      confidence: conf(Math.round((top.count / n) * 100)),
      sampleSize: n,
      learnedAt: todayStr(),
      source: "learned",
    });
  }
  // 内容形式
  if (analysis.topForms.length > 0) {
    const top = analysis.topForms[0];
    rules.push({
      id: "learned_format_top",
      category: "format",
      insight: `${top.form}形式爆款占比最高（${top.count}/${n}）`,
      suggestion: `增加${top.form}内容产出比例，强化优势形式`,
      confidence: conf(Math.round((top.count / n) * 100)),
      sampleSize: n,
      learnedAt: todayStr(),
      source: "learned",
    });
  }
  return rules;
}

// ============================================
// 主组件
// ============================================

export default function ContentOptimizationLoop() {
  const dashboard = useDashboardData();
  const { marketing, loading, lastUpdated, refresh } = dashboard;

  // 平台选择：all 或具体平台
  const [platformFilter, setPlatformFilter] = useState<MarketingPlatform | "all">("all");
  // 手动刷新中
  const [refreshing, setRefreshing] = useState(false);
  // 学习规则（localStorage 加载）
  const [learnedRules, setLearnedRules] = useState<OptimizationRule[]>([]);
  // 上一次持久化的学习规则 JSON（避免高频写入）
  const lastPersistedRef = useRef<string>("");

  // 初次挂载：加载本地学习规则
  useEffect(() => {
    const stored = loadLearnedRules();
    setLearnedRules(stored);
    lastPersistedRef.current = JSON.stringify(stored);
  }, []);

  // ============================================
  // 派生数据：聚合内容项（带分级）
  // ============================================

  /** 当前筛选范围下的所有内容项（带平台与分级） */
  const scoredContents = useMemo<ScoredContent[]>(() => {
    const platforms = Object.values(marketing.platforms);
    const list: ScoredContent[] = [];
    for (const p of platforms) {
      if (platformFilter !== "all" && p.platform !== platformFilter) continue;
      // 跳过降级的空平台（无真实数据）
      if (p.degraded) continue;
      for (const item of p.topContent) {
        list.push({
          ...item,
          platform: p.platform,
          grade: classifyContent(item),
        });
      }
    }
    return list;
  }, [marketing.platforms, platformFilter]);

  /** 效果分级统计 */
  const gradeStats = useMemo(() => {
    const stats: Record<ContentGrade, number> = { viral: 0, good: 0, normal: 0, poor: 0 };
    for (const c of scoredContents) stats[c.grade] += 1;
    const total = scoredContents.length;
    return { stats, total };
  }, [scoredContents]);

  /** 选中范围聚合指标（阅读/点赞/评论/转发/粉丝增长） */
  const aggregateMetrics = useMemo(() => {
    const platforms = Object.values(marketing.platforms);
    const scoped = platforms.filter(
      (p) => (platformFilter === "all" || p.platform === platformFilter) && !p.degraded
    );
    const sum = scoped.reduce(
      (acc, p) => {
        acc.views += p.metrics.views || 0;
        acc.likes += p.metrics.likes || 0;
        acc.comments += p.metrics.comments || 0;
        acc.shares += p.metrics.shares || 0;
        acc.followersGain += p.metrics.followersGain || 0;
        return acc;
      },
      { views: 0, likes: 0, comments: 0, shares: 0, followersGain: 0 }
    );
    return { ...sum, platformCount: scoped.length };
  }, [marketing.platforms, platformFilter]);

  /** 爆款特征分析 */
  const hotAnalysis = useMemo<HotFeatureAnalysis>(() => {
    const virals = scoredContents.filter((c) => c.grade === "viral");
    const n = virals.length;
    if (n === 0) {
      return {
        totalCount: 0,
        avgTitleLength: 0,
        numberRate: 0,
        questionRate: 0,
        topTimeSlots: [],
        topForms: [],
        topKeywords: [],
      };
    }
    // 标题长度
    const avgTitleLength = Math.round(
      virals.reduce((s, c) => s + (c.title?.length || 0), 0) / n
    );
    // 含数字 / 含疑问词比例
    const numberCount = virals.filter((c) => titleHasNumber(c.title)).length;
    const questionCount = virals.filter((c) => titleHasQuestion(c.title)).length;
    const numberRate = Math.round((numberCount / n) * 100);
    const questionRate = Math.round((questionCount / n) * 100);
    // 发布时段分布
    const slotCounter = new Map<string, number>();
    for (const c of virals) {
      const slot = getTimeSlot(c.publishedAt);
      slotCounter.set(slot, (slotCounter.get(slot) || 0) + 1);
    }
    const topTimeSlots = Array.from(slotCounter.entries())
      .map(([slot, count]) => ({ slot, count }))
      .sort((a, b) => b.count - a.count);
    // 内容形式分布（按平台推断）
    const formCounter = new Map<string, number>();
    for (const c of virals) {
      const form = PLATFORM_FORM[c.platform] || "其他";
      formCounter.set(form, (formCounter.get(form) || 0) + 1);
    }
    const topForms = Array.from(formCounter.entries())
      .map(([form, count]) => ({ form, count }))
      .sort((a, b) => b.count - a.count);
    // 高频选题词
    const kwCounter = new Map<string, number>();
    for (const c of virals) {
      for (const kw of extractKeywords(c.title)) {
        kwCounter.set(kw, (kwCounter.get(kw) || 0) + 1);
      }
    }
    const topKeywords = Array.from(kwCounter.entries())
      .map(([word, count]) => ({ word, count }))
      .filter((k) => k.count >= 1)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
    return {
      totalCount: n,
      avgTitleLength,
      numberRate,
      questionRate,
      topTimeSlots,
      topForms,
      topKeywords,
    };
  }, [scoredContents]);

  /** 当前筛选范围的可读标签（用于学习规则的洞察描述） */
  const scopeLabel = useMemo(
    () => (platformFilter === "all" ? "全平台" : PLATFORM_LABEL[platformFilter]),
    [platformFilter]
  );

  // ============================================
  // 自动学习：爆款分析结果沉淀为学习规则并持久化
  // 仅当爆款样本数变化或洞察变化时写入，避免 5s 自动刷新导致高频 IO
  // ============================================
  useEffect(() => {
    if (hotAnalysis.totalCount === 0) return;
    const newLearned = generateLearnedRules(hotAnalysis, scopeLabel);
    if (newLearned.length === 0) return;
    // 合并：保留其他 scope 已学习的规则（按 id 去重，新规则覆盖同 id 旧规则）
    setLearnedRules((prev) => {
      const map = new Map<string, OptimizationRule>();
      for (const r of prev) map.set(r.id, r);
      for (const r of newLearned) map.set(r.id, r);
      return Array.from(map.values());
    });
  }, [hotAnalysis, scopeLabel]);

  // 持久化学习规则（仅在内容变化时写入）
  useEffect(() => {
    const json = JSON.stringify(learnedRules);
    if (json === lastPersistedRef.current) return;
    lastPersistedRef.current = json;
    saveLearnedRules(learnedRules);
  }, [learnedRules]);

  /** 合并内置规则 + 学习规则（按 id 去重，学习规则优先） */
  const mergedRules = useMemo<OptimizationRule[]>(() => {
    const map = new Map<string, OptimizationRule>();
    for (const r of BUILTIN_RULES) map.set(r.id, r);
    for (const r of learnedRules) map.set(r.id, r);
    return Array.from(map.values());
  }, [learnedRules]);

  /** 按类别分组的规则 */
  const rulesByCategory = useMemo(() => {
    const groups: Record<RuleCategory, OptimizationRule[]> = {
      title: [],
      topic: [],
      timing: [],
      format: [],
    };
    for (const r of mergedRules) {
      groups[r.category].push(r);
    }
    // 每类内：学习规则在前，内置在后；置信度降序
    for (const cat of Object.keys(groups) as RuleCategory[]) {
      groups[cat].sort((a, b) => {
        if (a.source !== b.source) return a.source === "learned" ? -1 : 1;
        return b.confidence - a.confidence;
      });
    }
    return groups;
  }, [mergedRules]);

  /** 学习规则数量 */
  const learnedCount = useMemo(
    () => mergedRules.filter((r) => r.source === "learned").length,
    [mergedRules]
  );

  /** 手动刷新 */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  /** 是否处于空状态（无任何已发布内容） */
  const isEmpty = scoredContents.length === 0;

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <RotateCw size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">数据驱动内容优化闭环</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Content Optimization Loop · {gradeStats.total} 内容 · 爆款 {gradeStats.stats.viral} · 已学习 {learnedCount} 规则
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-50"
          >
            {refreshing ? (
              <Loader2 size={11} className="text-cyan-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-cyan-400" />
            )}
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
              {refreshing ? "同步中..." : "同步数据"}
            </span>
          </button>
        </div>
      </div>

      {/* 平台选择 */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-white/[0.04] flex-wrap">
        <Filter size={10} className="text-gray-500" />
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">平台：</span>
        {(
          [
            { id: "all", label: "全平台" },
            { id: "wechat_official", label: "公众号" },
            { id: "xiaohongshu", label: "小红书" },
            { id: "douyin_enterprise", label: "抖音企业" },
            { id: "douyin_personal", label: "抖音个人" },
            { id: "zhihu", label: "知乎" },
            { id: "bilibili", label: "B站" },
          ] as const
        ).map((f) => (
          <button
            key={f.id}
            onClick={() => setPlatformFilter(f.id)}
            className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
              platformFilter === f.id
                ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                : "text-gray-500 hover:text-gray-300 border border-transparent"
            }`}
          >
            {f.label}
          </button>
        ))}
        {lastUpdated && (
          <span className="ml-auto text-[8px] font-mono text-gray-600">
            最后同步 {new Date(lastUpdated).toLocaleTimeString("zh-CN", { hour12: false })}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-cyan-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载内容数据...</p>
        </div>
      ) : (
        <>
          {/* 闭环流程图 */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 mb-3">
              <RotateCw size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                优化闭环流程
              </span>
            </div>
            <div className="flex items-stretch gap-1">
              {LOOP_NODES.map((node, idx) => {
                const Icon = node.icon;
                return (
                  <div key={node.label} className="flex items-center flex-1 min-w-0">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: idx * 0.06 }}
                      className="flex-1 min-w-0 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-2 text-center"
                      style={{ borderTop: `2px solid ${node.color}` }}
                    >
                      <div className="flex items-center justify-center mb-1">
                        <div
                          className="w-6 h-6 rounded-md flex items-center justify-center"
                          style={{ background: `${node.color}20`, border: `1px solid ${node.color}40` }}
                        >
                          <Icon size={11} style={{ color: node.color }} />
                        </div>
                      </div>
                      <div className="text-[10px] font-mono text-gray-200">{node.label}</div>
                      <div className="text-[8px] font-mono text-gray-600 mt-0.5 truncate">{node.desc}</div>
                    </motion.div>
                    {idx < LOOP_NODES.length - 1 && (
                      <ArrowRight size={11} className="text-gray-600 mx-0.5 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
            {/* 回环提示 */}
            <div className="flex items-center justify-center gap-1 mt-2">
              <span className="text-[8px] font-mono text-gray-600 uppercase tracking-wider">
                再发布回到发布，形成持续迭代闭环
              </span>
            </div>
          </div>

          {/* 数据回收 KPI */}
          <div className="grid grid-cols-5 gap-2 p-4 border-b border-white/[0.04]">
            <MetricCard icon={BarChart3} label="阅读量" value={aggregateMetrics.views} color="#38BDF8" />
            <MetricCard icon={TrendingUp} label="点赞" value={aggregateMetrics.likes} color="#34D399" />
            <MetricCard icon={Activity} label="评论" value={aggregateMetrics.comments} color="#A78BFA" />
            <MetricCard icon={Send} label="转发" value={aggregateMetrics.shares} color="#FB923C" />
            <MetricCard icon={Sparkles} label="粉丝增长" value={aggregateMetrics.followersGain} color="#FBBF24" />
          </div>

          {isEmpty ? (
            <div className="py-10 text-center">
              <Database size={22} className="mx-auto text-gray-600 mb-2" />
              <p className="text-[11px] font-mono text-gray-500">
                暂无已发布内容数据
              </p>
              <p className="text-[9px] font-mono text-gray-600 mt-1">
                发布内容后，数据将自动同步并进入优化闭环
              </p>
            </div>
          ) : (
            <>
              {/* 内容效果排行榜 */}
              <div className="p-4 border-b border-white/[0.04]">
                <div className="flex items-center gap-2 mb-3">
                  <Award size={11} className="text-amber-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    内容效果排行榜（基于 {gradeStats.total} 条已发布内容）
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {(Object.values(GRADE_META) as GradeMeta[]).map((g) => {
                    const count = gradeStats.stats[g.key];
                    const pct = gradeStats.total > 0 ? Math.round((count / gradeStats.total) * 100) : 0;
                    const Icon = g.icon;
                    return (
                      <div
                        key={g.key}
                        className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-2"
                        style={{ borderTop: `2px solid ${g.color}` }}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <Icon size={10} style={{ color: g.color }} />
                          <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: g.color }}>
                            {g.label}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-[18px] font-mono font-bold" style={{ color: g.color }}>
                            {count}
                          </span>
                          <span className="text-[9px] font-mono text-gray-500">{pct}%</span>
                        </div>
                        {/* 占比条 */}
                        <div className="mt-1.5 h-1.5 rounded bg-white/[0.03] overflow-hidden">
                          <div
                            className="h-full rounded transition-all"
                            style={{
                              width: `${Math.max(2, pct)}%`,
                              background: `linear-gradient(90deg, ${g.color}80, ${g.color})`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 爆款特征分析 */}
              <div className="p-4 border-b border-white/[0.04]">
                <div className="flex items-center gap-2 mb-3">
                  <Activity size={11} className="text-violet-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    爆款特征分析（样本 {hotAnalysis.totalCount} 条）
                  </span>
                </div>
                {hotAnalysis.totalCount === 0 ? (
                  <p className="text-[11px] font-mono text-gray-600 text-center py-3">
                    暂无爆款内容（views &gt; 10000 且点赞率 &gt; 5%），无法分析特征
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {/* 标题长度 */}
                    <FeatureCard
                      icon={Type}
                      color="#38BDF8"
                      label="平均标题长度"
                      value={`${hotAnalysis.avgTitleLength} 字`}
                    />
                    {/* 含数字比例 */}
                    <FeatureCard
                      icon={Hash}
                      color="#34D399"
                      label="标题含数字"
                      value={`${hotAnalysis.numberRate}%`}
                    />
                    {/* 含疑问词比例 */}
                    <FeatureCard
                      icon={Lightbulb}
                      color="#A78BFA"
                      label="标题含疑问词"
                      value={`${hotAnalysis.questionRate}%`}
                    />
                    {/* 发布时段分布 */}
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5 col-span-1">
                      <div className="flex items-center gap-1 mb-1.5">
                        <Clock size={10} className="text-amber-400" />
                        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                          发布时段
                        </span>
                      </div>
                      {hotAnalysis.topTimeSlots.length === 0 ? (
                        <p className="text-[10px] font-mono text-gray-600">无数据</p>
                      ) : (
                        <div className="space-y-1">
                          {hotAnalysis.topTimeSlots.slice(0, 3).map((s) => {
                            const max = hotAnalysis.topTimeSlots[0].count || 1;
                            const w = Math.max(10, Math.round((s.count / max) * 100));
                            return (
                              <div key={s.slot} className="flex items-center gap-1.5">
                                <span className="w-20 text-[9px] font-mono text-gray-300 truncate">{s.slot}</span>
                                <div className="flex-1 h-2 rounded bg-white/[0.02] overflow-hidden">
                                  <div
                                    className="h-full rounded"
                                    style={{ width: `${w}%`, background: "linear-gradient(90deg, #FBBF2480, #FBBF24)" }}
                                  />
                                </div>
                                <span className="text-[9px] font-mono text-gray-400 w-4 text-right">{s.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* 内容形式分布 */}
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5 col-span-1">
                      <div className="flex items-center gap-1 mb-1.5">
                        <BarChart3 size={10} className="text-emerald-400" />
                        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                          内容形式
                        </span>
                      </div>
                      {hotAnalysis.topForms.length === 0 ? (
                        <p className="text-[10px] font-mono text-gray-600">无数据</p>
                      ) : (
                        <div className="space-y-1">
                          {hotAnalysis.topForms.slice(0, 3).map((f) => {
                            const max = hotAnalysis.topForms[0].count || 1;
                            const w = Math.max(10, Math.round((f.count / max) * 100));
                            return (
                              <div key={f.form} className="flex items-center gap-1.5">
                                <span className="w-16 text-[9px] font-mono text-gray-300 truncate">{f.form}</span>
                                <div className="flex-1 h-2 rounded bg-white/[0.02] overflow-hidden">
                                  <div
                                    className="h-full rounded"
                                    style={{ width: `${w}%`, background: "linear-gradient(90deg, #34D39980, #34D399)" }}
                                  />
                                </div>
                                <span className="text-[9px] font-mono text-gray-400 w-4 text-right">{f.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* 高频选题词 */}
                    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5 col-span-1">
                      <div className="flex items-center gap-1 mb-1.5">
                        <Tag size={10} className="text-violet-400" />
                        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                          高频选题词
                        </span>
                      </div>
                      {hotAnalysis.topKeywords.length === 0 ? (
                        <p className="text-[10px] font-mono text-gray-600">无数据</p>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {hotAnalysis.topKeywords.map((k) => (
                            <span
                              key={k.word}
                              className="px-1.5 py-0.5 rounded text-[9px] font-mono border border-violet-500/30 bg-violet-500/10 text-violet-300"
                            >
                              {k.word}
                              <span className="text-violet-500 ml-1">×{k.count}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* AI 优化建议 */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb size={11} className="text-amber-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    AI 优化建议（内置 {BUILTIN_RULES.length} 条 + 学习 {learnedCount} 条）
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(RULE_CATEGORY_META) as RuleCategory[]).map((cat) => {
                    const meta = RULE_CATEGORY_META[cat];
                    const rules = rulesByCategory[cat];
                    const Icon = meta.icon;
                    return (
                      <div
                        key={cat}
                        className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5"
                        style={{ borderLeft: `2px solid ${meta.color}` }}
                      >
                        <div className="flex items-center gap-1 mb-1.5">
                          <Icon size={11} style={{ color: meta.color }} />
                          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
                            {meta.label}
                          </span>
                        </div>
                        {rules.length === 0 ? (
                          <p className="text-[10px] font-mono text-gray-600">暂无建议</p>
                        ) : (
                          <div className="space-y-1.5">
                            {rules.map((r) => (
                              <div key={r.id} className="rounded bg-white/[0.02] px-2 py-1.5">
                                <div className="flex items-center gap-1 mb-0.5">
                                  <span
                                    className="px-1 rounded text-[8px] font-mono uppercase tracking-wider"
                                    style={{
                                      background: r.source === "learned" ? "#34D39920" : "#6B728020",
                                      color: r.source === "learned" ? "#34D399" : "#9CA3AF",
                                    }}
                                  >
                                    {r.source === "learned" ? "学习" : "内置"}
                                  </span>
                                  {r.sampleSize > 0 && (
                                    <span className="text-[8px] font-mono text-gray-600">
                                      样本 {r.sampleSize}
                                    </span>
                                  )}
                                  <span className="ml-auto text-[8px] font-mono text-gray-500">
                                    置信 {r.confidence}%
                                  </span>
                                </div>
                                <div className="text-[10px] font-mono text-gray-300 leading-snug">
                                  {r.insight}
                                </div>
                                <div className="text-[9px] font-mono text-gray-500 leading-snug mt-0.5">
                                  建议：{r.suggestion}
                                </div>
                                {/* 置信度条 */}
                                <div className="mt-1 h-1 rounded bg-white/[0.03] overflow-hidden">
                                  <div
                                    className="h-full rounded"
                                    style={{
                                      width: `${r.confidence}%`,
                                      background: `linear-gradient(90deg, ${meta.color}80, ${meta.color})`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </motion.div>
  );
}

// ============================================
// 指标小卡片（数据回收 KPI）
// ============================================
interface MetricCardProps {
  icon: typeof BarChart3;
  label: string;
  value: number;
  color: string;
}

function MetricCard({ icon: Icon, label, value, color }: MetricCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value.toLocaleString("zh-CN")}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 爆款特征小卡片（单值展示）
// ============================================
interface FeatureCardProps {
  icon: typeof Type;
  color: string;
  label: string;
  value: string;
}

function FeatureCard({ icon: Icon, color, label, value }: FeatureCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-2"
      style={{ borderTop: `2px solid ${color}` }}
    >
      <div className="flex items-center gap-1 mb-1">
        <Icon size={10} style={{ color }} />
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-[16px] font-mono font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
